import { createCodedError } from '~/common/errors/CodedError';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { esignature } from '~/services/serviceInstances';
import { Nullable } from '~/types/Nullable';

type Request = {
  return_to: string;
};

type ServerRequest = {
  data: {
    type: string;
    attributes: {
      return_url: string;
    };
  };
};

type ServerResponse = {
  type: string;
  id: string;
  links: {
    authorize: Nullable<string>;
  };
};

export type EsignatureAuthenticateResponse = {
  authenticateUrl: Nullable<string>;
};

export const getAuthenticate = async (
  request: Request,
): Promise<EsignatureAuthenticateResponse> => {
  try {
    const response: ServerResponse = await esignature.post(
      `/authorize`,
      mapRequest(request),
      { withCredentials: true },
    );
    return mapResponse(response);
  } catch (error) {
    // Delete this section when API changes its response code from 302
    if (
      testIsAxiosError<ServerResponse>(error) &&
      error.status === 302 &&
      error.response?.data?.links?.authorize
    ) {
      return mapResponse(error.response.data);
    }

    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors[0];
      throw createCodedError({
        message: errorResponse.detail ?? errorResponse.title,
        statusCode: errorResponse.status,
        title: errorResponse.title,
      });
    }

    throw error;
  }
};

const mapRequest = (request: Request): ServerRequest => {
  return {
    data: {
      type: 'authorizations',
      attributes: {
        return_url: request.return_to,
      },
    },
  };
};

const mapResponse = (
  serverResponse: ServerResponse,
): EsignatureAuthenticateResponse => {
  const authenticateUrl = serverResponse.links.authorize;

  return {
    authenticateUrl,
  };
};
